import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Check, CreditCard, Rocket, ShieldCheck, Download } from 'lucide-react';
import { toast } from 'sonner';
import api, { formatApiError } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { ZapBolt } from '@/components/BoostaBolt';
import { HoloCard } from '@/components/HoloCard';

const ORDER = ['starter', 'growth', 'pro', 'business'];

export default function Payments() {
  const [plans, setPlans] = useState({});
  const [topups, setTopups] = useState({});
  const [txns, setTxns] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [params, setParams] = useSearchParams();
  const { user, refreshUser } = useAuth();

  const load = async () => {
    const [p, t] = await Promise.all([api.get('/payments/plans'), api.get('/payments/transactions')]);
    setPlans(p.data.plans); setTopups(p.data.topups); setTxns(t.data);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const sid = params.get('session_id');
    if (!sid) return;
    let attempts = 0;
    const poll = async () => {
      attempts++;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === 'paid') {
          toast.success('Payment confirmed — plan activated.');
          setParams({}); await refreshUser(); await load();
          return;
        }
        if (data.status === 'expired') { toast.error('Session expired.'); setParams({}); return; }
        if (attempts < 8) setTimeout(poll, 2000);
      } catch (_) { if (attempts < 8) setTimeout(poll, 2000); }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkout = async (pkgId, trial = false) => {
    setBusyId(pkgId + (trial ? '-trial' : ''));
    try {
      const { data } = await api.post('/payments/checkout', { package_id: pkgId, origin_url: window.location.origin, trial });
      window.location.href = data.url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusyId(null); }
  };

  const downloadReceipt = async (t) => {
    try {
      const res = await api.get(`/payments/receipt/${t.txn_id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `boosta-receipt-${t.txn_id}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('Could not download receipt'); }
  };

  const openPortal = async () => {
    setBusyId('portal');
    try {
      const { data } = await api.post('/payments/portal', { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusyId(null); }
  };

  const currentPlan = user?.plan || 'free';
  const status = user?.plan_status;
  const trialLeft = user?.trial_ends_at ? Math.max(0, Math.ceil((new Date(user.trial_ends_at) - Date.now()) / 86400000)) : null;

  return (
    <div className="space-y-8" data-testid="payments-root">
      <div>
        <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-boosta-cyan mb-2">Payments</div>
        <h1 className="font-display font-black text-4xl tracking-tighter text-3d">Plans, ZAP &amp; billing</h1>
      </div>

      {/* Current plan banner */}
      <div className="glass rounded-2xl p-5 flex flex-wrap items-center justify-between gap-4" data-testid="current-plan-banner">
        <div className="flex items-center gap-3">
          <ShieldCheck className="w-5 h-5 text-boosta-emerald"/>
          <div>
            <div className="text-sm">
              Current plan: <span className="font-display font-bold text-boosta-cyan capitalize" data-testid="current-plan-name">{plans[currentPlan]?.name || 'Free'}</span>
              {status === 'trialing' && <span className="ml-2 text-[10px] uppercase tracking-wider font-bold bg-boosta-yellow/15 text-boosta-yellow px-2 py-0.5 rounded-full">Trial · {trialLeft}d left</span>}
              {status === 'active' && <span className="ml-2 text-[10px] uppercase tracking-wider font-bold bg-boosta-emerald/15 text-boosta-emerald px-2 py-0.5 rounded-full">Active</span>}
            </div>
            <div className="text-xs text-white/45 mt-0.5">{user?.zap_credits?.toLocaleString()} ZAP available</div>
          </div>
        </div>
        {status === 'trialing' && (
          <button onClick={()=>checkout(currentPlan)} className="btn-primary rounded-full px-5 py-2 text-sm" data-testid="upgrade-from-trial">Subscribe to keep {plans[currentPlan]?.name}</button>
        )}
        {(status === 'active' || status === 'trialing' || status === 'canceled') && (
          <button onClick={openPortal} disabled={busyId==='portal'} className="btn-ghost rounded-full px-5 py-2 text-sm inline-flex items-center gap-2" data-testid="manage-billing">
            <CreditCard className="w-4 h-4"/>{busyId==='portal'?'Opening…':'Manage billing'}
          </button>
        )}
      </div>

      <div>
        <div className="font-display font-bold text-xl mb-4">Subscription plans</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {ORDER.filter(id=>plans[id]).map((id) => {
            const p = plans[id];
            const isCurrent = currentPlan === id;
            return (
              <HoloCard key={id} className={`glass holo-edge rounded-2xl p-6 flex flex-col ${id==='pro'?'glow-cyan':''} ${isCurrent?'border-boosta-emerald/50':''}`} data-testid={`plan-${id}`}>
                <div className="flex items-center justify-between mb-1">
                  {id==='pro' && <span className="text-[9px] font-bold uppercase tracking-wider text-boosta-ink bg-boosta-cyan rounded-full px-2.5 py-0.5">Popular</span>}
                  {isCurrent && <span className="text-[9px] font-bold uppercase tracking-wider text-boosta-emerald border border-boosta-emerald/40 rounded-full px-2.5 py-0.5 ml-auto" data-testid={`plan-current-${id}`}>Your plan</span>}
                </div>
                <div className="font-display font-bold text-lg">{p.name}</div>
                <div className="text-[11px] text-white/45 mb-3 h-8">{p.tagline}</div>
                <div className="flex items-baseline gap-1">
                  <span className="font-display font-black text-4xl">£{p.price_gbp}</span>
                  <span className="text-white/40 text-xs">/{p.interval}</span>
                </div>
                <ul className="mt-4 space-y-2 flex-1">
                  {p.benefits.map((b,i)=>(
                    <li key={b} className="flex items-start gap-2 text-xs text-white/70"><Check className="w-3.5 h-3.5 text-boosta-emerald mt-0.5 shrink-0"/>{b}</li>
                  ))}
                </ul>
                {isCurrent && status !== 'trialing' ? (
                  <div className="mt-5 w-full text-center text-xs text-boosta-emerald font-bold py-2.5 border border-boosta-emerald/30 rounded-xl">Active subscription</div>
                ) : (
                  <>
                    <button disabled={!!busyId} onClick={()=>checkout(id)} className={`mt-5 w-full rounded-xl py-2.5 text-sm font-bold disabled:opacity-50 ${id==='pro'?'btn-primary':'btn-ghost'}`} data-testid={`subscribe-${id}`}>
                      {busyId===id ? 'Loading…' : 'Subscribe'}
                    </button>
                    {(currentPlan === 'free' && status !== 'trialing') && (
                      <button disabled={!!busyId} onClick={()=>checkout(id, true)} className="mt-2 w-full text-xs text-boosta-cyan hover:underline inline-flex items-center justify-center gap-1 disabled:opacity-50" data-testid={`trial-${id}`}>
                        {busyId===id+'-trial' ? 'Starting…' : <><Rocket className="w-3 h-3"/> Start 7-day free trial (card required)</>}
                      </button>
                    )}
                  </>
                )}
              </HoloCard>
            );
          })}
        </div>
      </div>

      <div>
        <div className="font-display font-bold text-xl mb-4">ZAP top-ups</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Object.entries(topups).map(([id, t]) => (
            <HoloCard key={id} className="glass rounded-2xl p-5" data-testid={`topup-${id}`}>
              <div className="flex justify-between items-start mb-3">
                <ZapBolt size={22}/>
                <span className="text-xs uppercase tracking-wider text-white/40">One-time</span>
              </div>
              <div className="font-display font-black text-3xl">{t.zap.toLocaleString()}</div>
              <div className="text-xs text-white/50 mt-1">ZAP credits</div>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="font-bold text-xl">£{t.price_gbp}</span>
              </div>
              <button disabled={!!busyId} onClick={()=>checkout(id)} className="mt-3 w-full btn-ghost rounded-xl py-2 text-sm disabled:opacity-50" data-testid={`buy-${id}`}>
                {busyId===id ? '…' : 'Buy'}
              </button>
            </HoloCard>
          ))}
        </div>
      </div>

      <div className="glass rounded-2xl p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="font-display font-bold text-xl">Transaction history</div>
          <CreditCard className="w-4 h-4 text-white/40"/>
        </div>
        {txns.length === 0 ? (
          <div className="text-white/45 text-sm py-6 text-center">No transactions yet.</div>
        ) : (
          <div className="space-y-2">
            {txns.map(t => (
              <div key={t.txn_id} className="flex justify-between items-center py-3 px-4 border border-white/5 rounded-xl">
                <div>
                  <div className="font-medium capitalize">{t.kind} · {t.package_id}</div>
                  <div className="text-xs text-white/45">{new Date(t.created_at).toLocaleString()}</div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <div className="font-bold">£{t.amount.toFixed(2)}</div>
                    <div className={`text-[10px] uppercase tracking-wider font-bold ${t.payment_status==='paid'?'text-boosta-emerald':'text-white/40'}`}>{t.payment_status}</div>
                  </div>
                  {t.payment_status === 'paid' && (
                    <button onClick={()=>downloadReceipt(t)} title="Download PDF receipt"
                      className="btn-ghost rounded-lg p-2 text-boosta-cyan hover:text-white" data-testid={`receipt-${t.txn_id}`}>
                      <Download className="w-4 h-4"/>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
